// angular dependencies
import { inject } from '@angular/core';

// third-party dependencies
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// fantaz dependencies
import {
  AuthService,
  AuthServiceInjectionToken,
} from '@fantaz/shared/data-access/api';

import { CoreRouterActions } from '@fantaz/core/router';
import { ToastService } from '@fantaz/shared/utils/toast';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs';
import {
  confirmResetPassword,
  confirmResetPasswordFailure,
  confirmResetPasswordSuccess,
  resendResetPasswordCode,
  resetPassword,
  sendResetPasswordCode,
  sendResetPasswordCodeFailure,
  sendResetPasswordCodeSuccess,
} from './reset-password.actions';
import { selectResetPasswordArguments } from './reset-password.selectors';

export class ResetPasswordEffects {
  #actions$: Actions = inject(Actions);
  #store: Store = inject(Store);
  #toastService: ToastService = inject(ToastService);

  #authService: AuthService = inject(AuthServiceInjectionToken);

  resetPassword$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(resetPassword),
      map(() => {
        return CoreRouterActions.navigate([
          '',
          {
            outlets: {
              dialog: ['reset-password-verification'],
            },
          },
        ]);
      }),
    ),
  );
  sendResetPassword$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sendResetPasswordCode, resendResetPasswordCode),
      withLatestFrom(
        this.#store.select(selectResetPasswordArguments),
        (_, { username, password }) => {
          return { username, password };
        },
      ),
      switchMap((payload) => {
        if (!payload.username) {
          return [
            CoreRouterActions.navigate([
              '',
              {
                outlets: {
                  dialog: ['reset-password'],
                },
              },
            ]),
          ];
        }
        return this.#authService
          .resetPassword({
            username: payload.username as string,
          })
          .pipe(
            map((data) => sendResetPasswordCodeSuccess({ data })),
            catchError((error: any) => {
              return [sendResetPasswordCodeFailure({ error })];
            }),
          );
      }),
      catchError((error: any) => {
        return [sendResetPasswordCodeFailure({ error })];
      }),
    ),
  );
  confirmResetPassword$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(confirmResetPassword),
      withLatestFrom(
        this.#store.select(selectResetPasswordArguments),
        ({ code }, { username, password }) => {
          return { username, password, code };
        },
      ),
      switchMap((payload) => {
        if (!payload.username) {
          return [
            CoreRouterActions.navigate([
              '',
              {
                outlets: {
                  dialog: ['reset-password'],
                },
              },
            ]),
          ];
        }
        return this.#authService
          .confirmResetPassword({
            newPassword: payload.password as string,
            username: payload.username as string,
            confirmationCode: payload.code,
          })
          .pipe(
            map((data) => confirmResetPasswordSuccess()),
            catchError((error: any) => {
              return [confirmResetPasswordFailure({ error })];
            }),
          );
      }),
      catchError((error: any) => {
        return [confirmResetPasswordFailure({ error })];
      }),
    ),
  );
  sendResetPasswordCodeSuccess$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(sendResetPasswordCodeSuccess),

        tap((data) => {
          this.#toastService.openSnackBar('Code sent successful');
        }),
      ),
    { dispatch: false },
  );

  confirmResetPasswordSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(confirmResetPasswordSuccess),
      map(() => {
        this.#toastService.openSnackBar('Password changed successful');
        return CoreRouterActions.navigate([
          '',
          {
            outlets: {
              dialog: null,
            },
          },
        ]);
      }),
    ),
  );

  confirmResetPasswordFailure$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(confirmResetPasswordFailure, sendResetPasswordCodeFailure),
      switchMap(({ error }) => {
        if (error && error['name'] !== 'LimitExceededException') {
          return [];
        } else {
          return [
            CoreRouterActions.navigate([
              '',
              {
                outlets: {
                  dialog: ['reset-password-unsuccessful'],
                },
              },
            ]),
          ];
        }
      }),
    ),
  );
}
